import PolicyLayout, { PolicySection, formatUpdatedAt } from '@/components/PolicyLayout';
import { listOps, newBlockId, useEditablePage } from '@/hooks/useEditablePage';
import EditableText from './EditableText';
import RichText from './RichText';
import IconPicker from './IconPicker';
import EditBar from './EditBar';
import PageState from './PageState';
import { AddBlockButton, BlockControls } from './BlockControls';

/**
 * صفحة سياسة (الخصوصية، الشروط، الاستبدال): عنوان + أقسام.
 * كل قسم: أيقونة + عنوان + نص منسّق + ملاحظة صغيرة اختيارية.
 */
const PolicyPage = ({ slug }) => {
  const page = useEditablePage(slug);
  const { data, editing, update } = page;

  if (!data) return <PageState page={page} />;

  const sections = data.content?.sections ?? [];
  const ops = listOps(update, ['content', 'sections']);
  const addSection = (index) =>
    ops.insert(index, { id: newBlockId(), icon: 'Info', title: 'قسم جديد', html: '<p></p>', note: '' });

  return (
    <>
      <PolicyLayout
        iconSlot={
          (editing || data.icon) && (
            <IconPicker value={data.icon} editing={editing} onChange={(v) => update(['icon'], v)}>
              {(Icon) => <Icon className="w-8 h-8 text-burgundy-500" />}
            </IconPicker>
          )
        }
        title={
          <EditableText editing={editing} value={data.title} onChange={(v) => update(['title'], v)} placeholder="عنوان الصفحة" />
        }
        subtitle={
          (editing || data.subtitle) && (
            <EditableText
              editing={editing}
              multiline
              value={data.subtitle}
              onChange={(v) => update(['subtitle'], v)}
              placeholder="وصف قصير تحت العنوان (اختياري)"
            />
          )
        }
        updatedAt={formatUpdatedAt(data.updated_at)}
      >
        {sections.map((section, index) => (
          <div key={section.id} className="space-y-6">
            <PolicySection
              iconSlot={
                (editing || section.icon) && (
                  <IconPicker
                    value={section.icon}
                    editing={editing}
                    onChange={(v) => update(['content', 'sections', index, 'icon'], v)}
                  >
                    {(Icon) => <Icon className="w-6 h-6 text-burgundy-500 flex-shrink-0" />}
                  </IconPicker>
                )
              }
              title={
                <EditableText
                  editing={editing}
                  value={section.title}
                  onChange={(v) => update(['content', 'sections', index, 'title'], v)}
                  placeholder="عنوان القسم"
                  className="flex-1"
                />
              }
              actions={
                editing && <BlockControls index={index} count={sections.length} onMove={ops.move} onRemove={ops.remove} />
              }
            >
              <RichText
                editing={editing}
                html={section.html}
                onChange={(v) => update(['content', 'sections', index, 'html'], v)}
                placeholder="اكتبي محتوى القسم..."
              />
              {(editing || section.note) && (
                <EditableText
                  as="p"
                  editing={editing}
                  multiline
                  value={section.note}
                  onChange={(v) => update(['content', 'sections', index, 'note'], v)}
                  placeholder="ملاحظة صغيرة بخط رمادي (اختياري)"
                  className="text-sm text-gray-600 dark:text-gray-400"
                />
              )}
            </PolicySection>
            {editing && <AddBlockButton label="إضافة قسم هنا" onClick={() => addSection(index + 1)} />}
          </div>
        ))}
        {editing && sections.length === 0 && <AddBlockButton label="إضافة قسم" onClick={() => addSection(0)} />}
      </PolicyLayout>
      <EditBar page={page} />
    </>
  );
};

export default PolicyPage;
